import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { OtpInput } from '@/components/OtpInput'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_RESETNEW_COPY } from '@/copy/registry'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/** `a-resetnew` — completes a reset against a mailed code, never a row identifier (§9.h B1).
 * `identityCompletePasswordReset` ends every session (§3.1f); the client hands off to sign-in
 * with the reason stated. */
export function AResetnew({ email, onReset }: { email: string; onReset: () => void }) {
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const reset = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/password/reset', { body: { email, code, new_password: newPassword } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: onReset,
  })

  const problem = reset.error instanceof ApiProblemError ? reset.error.problem : undefined
  const mismatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword !== confirmPassword

  return (
    <AuthCard unitId={unitId('a-resetnew')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_RESETNEW_COPY.heading}</h3>
      <AuthCardLead>{A_RESETNEW_COPY.leadTemplate(email)}</AuthCardLead>

      <FieldStack>
        <Field label={A_RESETNEW_COPY.codeFieldLabel}>
          <OtpInput value={code} onChange={setCode} />
        </Field>
      </FieldStack>

      <FieldStack>
        <Field label={A_RESETNEW_COPY.newPasswordLabel}>
          <input type="password" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        </Field>
        <Field label={A_RESETNEW_COPY.confirmPasswordLabel}>
          <input
            type="password"
            className={TEXT_INPUT_CLASSNAME}
            style={TEXT_INPUT_STYLE}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </Field>
      </FieldStack>

      <Note>{A_RESETNEW_COPY.passwordRule}</Note>
      <div className="mb-9">
        <Strip>{A_RESETNEW_COPY.everySessionEndsNote}</Strip>
      </div>

      {mismatch && <FormError>{A_RESETNEW_COPY.passwordMismatch}</FormError>}
      {problem && <AResetnewRefusal problem={problem} />}

      <Button
        data-act={actionId('session.resetPassword')}
        full
        disabled={reset.isPending || mismatch || code.length < 6 || !newPassword || !confirmPassword}
        onClick={() => reset.mutate()}
      >
        {reset.isPending ? 'Setting…' : A_RESETNEW_COPY.submitButton}
      </Button>

      <Note>{A_RESETNEW_COPY.lockoutNote}</Note>
    </AuthCard>
  )
}

function AResetnewRefusal({ problem }: { problem: Problem }) {
  switch (problem.refusal_kind) {
    case 'CREDENTIAL_REJECTED':
      return <FormError>{A_RESETNEW_COPY.rejectedCopy}</FormError>
    case 'VALIDATION_FAILED':
      return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
    case 'RATE_LIMITED':
      return <RateLimitedNotice problem={problem} />
    default:
      return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
  }
}
